import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router'
import { useSelector } from 'react-redux'
import { Card, CardContent, CircularProgress, Grid, Divider, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Avatar, CardHeader, CardActionArea, Menu, MenuItem, ListItemIcon, ListItemText, Link, List, ListItem, Paper } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import moment from 'moment'
import localization from 'moment/locale/pt-br'

import AdjustIcon from '@material-ui/icons/Adjust'
import LockIcon from '@material-ui/icons/Lock'
import BusinessIcon from '@material-ui/icons/Business'
import ScheduleIcon from '@material-ui/icons/Schedule'
import DescriptionIcon from '@material-ui/icons/Description'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import CreateIcon from '@material-ui/icons/Create'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import { CloudDownload } from '@material-ui/icons'

import { useAuth } from 'hooks/useAuth'
import { useExameSolicitadoService } from 'hooks/useExameSolicitadoService'
import { useExameRealizadoService } from 'hooks/useExameRealizadoService'
import { useDescricaoTecnicaVestigioService } from 'hooks/useDescricaoTecnicaVestigioService'
import { useExameLocalService } from 'hooks/useExameLocalService'
import { useUsuarioService } from 'hooks/useUsuarioService'
import useRouter from 'utils/useRouter'
import { hasDocumentoSolicitacao } from 'utils/helpers'
import { getLabelTipoVestigioEspecifico } from 'helpers/labels'
import tiposDeExame from 'globals/tiposDeExame'

import { Page, Header, ObjetoAvatar, SecaoGenerica, BaseCard, CardField } from 'components'
import { ExibirArquivo } from 'components/ExibirArquivo'
import { ConfirmarIdentidade } from 'components/ConfirmarIdentidade'
import { ListaInformacoes } from 'views/Informacao/ListaInformacoes'
import { CardSolicitacaoExame } from 'components/CardSolicitacaoExame'
import { HistoricoOperacoes } from 'components/HistoricoOperacoes'
import { CardCaso } from 'components/Cards/CardCaso'
import { CardVestigio } from 'components/Cards/CardVestigio'
import { CardCadaver } from 'components/Cards/CardCadaver'
import { CardPericiando } from 'components/Cards/CardPericiando'
import { CardLocal } from 'components/Cards/CardLocal'
import { CardVeiculo } from 'components/Cards/CardVeiculo'

import { MenuAcoes } from './components'
import { useStyles } from './ExameSolicitado.styles'

const StyledMenu = withStyles((theme) => ({
  paper: {
    border: `1px solid ${theme.palette.divider}`,
  },
}))((props) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center',
    }}
    {...props}
  />
))

const StyledMenuItem = withStyles((theme) => ({
  root: {
    '&:focus': {
      backgroundColor: theme.palette.secondary.main,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem)

const RenderQuesitos = ({ quesitos, classes }) => {
  const quantidadeQuesitos = quesitos?.length || 0
  if (quantidadeQuesitos === 0) return (<Typography className={classes.metaValue}>NENHUM QUESITO REGISTRADO NO CADASTRO DESTE EXAME</Typography>)

  if (quantidadeQuesitos < 6) {
    return (
      <ul className={classes.lista}>
        {quesitos.map((q, index) => (
          <li key={q.id} className={classes.listaItem}>
            <Typography className={classes.metaValue}>
              {`${index + 1}. ${q.quesito}`}
            </Typography>
          </li>
        ))}
      </ul>
    )
  }

  const quantidadePorColuna = Math.ceil(quantidadeQuesitos / 2)
  const colunas = [
    quesitos.slice(0, quantidadePorColuna),
    quesitos.slice(quantidadePorColuna),
  ]

  return (
    <div className={classes.listaColunas}>
      {colunas.map((coluna, colunaIndex) => (
        <ul key={colunaIndex} className={classes.lista}>
          {coluna.map((q, index) => (
            <li key={q.id} className={classes.listaItem}>
              <Typography className={classes.metaValue}>
                {`${colunaIndex * quantidadePorColuna + index + 1}. ${q.quesito}`}
              </Typography>
            </li>
          ))}
        </ul>
      ))}
    </div>
  )
}
export const ExameSolicitado = (props) => {
  const { route } = props
  moment.locale('pt-br', localization)
  const classes = useStyles()
  const params = useParams()
  const router = useRouter()
  const { currentUser } = useAuth()

  const exameSolicitadoService = useExameSolicitadoService()
  const exameRealizadoService = useExameRealizadoService()
  const descricaoTecnicaService = useDescricaoTecnicaVestigioService()
  const exameLocalService = useExameLocalService()
  const usuarioService = useUsuarioService()

  const permissaoAcoesExaminador = useSelector((state) => state.exameSolicitado.permissaoAcoesExaminador)
  const permissaoAssumirExame = useSelector((state) => state.exameSolicitado.permissaoAssumirExame)

  const [exameSolicitado, setExameSolicitado] = useState(null)
  const [processandoExameSolicitado, setProcessandoExameSolicitado] = useState(true)
  const [isObjetosAssociados, setIsObjetosAssociados] = useState(false)
  const [descricoesTecnicas, setDescricoesTecnicas] = useState([])
  const [dadosExameLocal, setDadosExameLocal] = useState()
  const [processandoDownload, setProcessandoDownload] = useState(false)
  const [refreshHistorico, setRefreshHistorico] = useState(false)
  const [anchorElDocumento, setAnchorElDocumento] = useState(null)
  const [openConfirmarIdentidade, setOpenConfirmarIdentidade] = useState(false)

  const listarExameSolicitado = useCallback(async () => {
    const result = await exameSolicitadoService.listarExameSolicitadoAsync(params.id, setProcessandoExameSolicitado)
    setExameSolicitado(result)
    setRefreshHistorico((prev) => !prev)
  }, [params.id])

  const fetchDescricoesTecnicas = useCallback(async () => {
    if (exameSolicitado?.id) {
      const tipoExameRealizado = exameSolicitado.exReal?.tipo
      if (tipoExameRealizado === tiposDeExame.CONSTATACAO) {
        const response = await descricaoTecnicaService.listarDescricoesTecnicasAtivasDeExameSolicitadoComAnalisesConstatacaoAsync(exameSolicitado.id)
        setDescricoesTecnicas(response)
      } else if (
        tipoExameRealizado === tiposDeExame.PESQUISA_THC_COCAINA ||
        tipoExameRealizado === tiposDeExame.QUIMICO_ANABOLIZANTES ||
        tipoExameRealizado === tiposDeExame.QUIMICO_OUTRAS_SUBSTANCIAS ||
        tipoExameRealizado === tiposDeExame.QUIMICO_PESQUISA_AGROTOXICOS ||
        tipoExameRealizado === tiposDeExame.QUIMICO_PESQUISA_SOLVENTES ||
        tipoExameRealizado === tiposDeExame.QUIMICO_PRODUTO_FARMACEUTICO ||
        tipoExameRealizado === tiposDeExame.QUIMICO_PESQUISA_INFLAMAVEIS
      ) {
        const response = await descricaoTecnicaService.listarDescricoesTecnicasAtivasComAnalisesDeExameQuimicoAsync(exameSolicitado.id)
        setDescricoesTecnicas(response)
      } else {
        setDescricoesTecnicas([])
      }
    }
  }, [exameSolicitado?.id, exameSolicitado?.exReal?.tipo])

  const verificarObjetosAssociados = (exame) => {
    setIsObjetosAssociados((exame.vests && exame.vests.length) || (exame.cadavs && exame.cadavs.length) || (exame.perics && exame.perics.length) || (exame.locais && exame.locais.length) || (exame.veiculos && exame.veiculos.length))
  }

  const fetchExameLocal = useCallback(async () => {
    await exameLocalService.listarExameLocalComAnalise(exameSolicitado?.exReal?.id, exameSolicitado?.exReal?.tipo, setDadosExameLocal)
  }, [exameSolicitado?.exReal?.id])

  useEffect(() => {
    listarExameSolicitado()
  }, [listarExameSolicitado])

  useEffect(() => {
    if (exameSolicitado) {
      verificarObjetosAssociados(exameSolicitado)
      if (exameSolicitado.id) {
        usuarioService.setPermissaoAcoesExaminador(exameSolicitado, currentUser)
      }
      fetchDescricoesTecnicas()
      fetchExameLocal()
    }
  }, [exameSolicitado, currentUser, fetchDescricoesTecnicas, fetchExameLocal])

  const handleClickBotaoAcoesDocumento = (event) => {
    event.stopPropagation()
    setAnchorElDocumento(event.currentTarget)
  }

  const handleCloseMenuDocumento = () => {
    setAnchorElDocumento(null)
  }

  const handleClickAssinarDocumento = async () => {
    handleCloseMenuDocumento()
    await exameSolicitadoService.assinarExameSolicitado(exameSolicitado.id)
    await listarExameSolicitado()
  }

  const handleDownloadDocumentoExameSolicitado = async () => {
    handleCloseMenuDocumento()
    await exameSolicitadoService.downloadPDFDocumentoExameSolicitado(exameSolicitado.id)
  }

  const handleClickOpenConfirmarIdentidade = () => {
    setOpenConfirmarIdentidade(true)
  }

  const verificarUsuarioAutoridadeSolicitante = (idUsuario) => {
    if (!idUsuario || !exameSolicitado?.solEx?.id) {
      return false
    }
    if (idUsuario == exameSolicitado.solEx.id_autoridade_requisitante) {
      return true
    }
    return false
  }

  const handleDownloadPDFErrata = useCallback(
    async (errata) => {
      await exameRealizadoService.downloadArquivoErrata(errata.id, errata.identificador, setProcessandoDownload)
      setRefreshHistorico((prev) => !prev)
    },
    []
  )

  const handleClickCadaver = (id) => {
    router.history.push(`/casos/caso/cadaver/detalhar/${id}`)
  }

  const handleClickPericiando = (id) => {
    router.history.push(`/casos/caso/periciando/detalhar/${id}`)
  }

  const handleClickVeiculo = (id) => {
    router.history.push(`/casos/caso/veiculo/detalhar/${id}`)
  }

  const handleClickLocal = (id) => {
    router.history.push(`/casos/caso/local/detalhar/${id}`)
  }

  const handleClickVestigio = (id) => {
    router.history.push(`/casos/caso/vestigio/detalhar/${id}`)
  }

  const handleClickExameLocal = (id) => {
    router.history.push(`/casos/caso/solicitacao/exameSolicitado/exameLocal/${id}`)
  }

  const handleClickCaso = (id, novaAba) => {
    if (novaAba) {
      window.open(`/casos/caso/detalhar/${id}`, '_blank', 'noreferrer')
    } else {
      router.history.push(`/casos/caso/detalhar/${id}`)
    }
  }

  const dadosQuantitativosAgrupados = useCallback((descricoes) => {
    const dadosAgrupados = []
    descricoes.forEach((dt) => {
      const descricao = {
        vestigio: dt.vest?.identificador || '',
        descricoes: [dt],
      }
      let dadoJaAdicionado = false
      dadosAgrupados.forEach((da, index) => {
        if (da.vestigio === descricao.vestigio) {
          dadoJaAdicionado = true
          dadosAgrupados[index].descricoes.push(descricao.descricoes[0])
        }
      })
      if (!dadoJaAdicionado) {
        dadosAgrupados.push(descricao)
      }
    })
    return dadosAgrupados
  }, [])

  return (
    <Page className={classes.root} title={`${route.secao} - SIGEP`}>
      <Header titulo={`${route.secao}`} overline={route.secao} />

      <div className={classes.resultsWrapper}>
        {!exameSolicitado ? (
          <div className={classes.carregando}>
            <CircularProgress disableShrink />
          </div>
        ) : (
          <>
            <Card className={classes.headerCard}>
              <CardContent style={{ padding: '24px' }}>
                <Grid container spacing={3} alignItems="center" justifyContent="space-between">
                  <Grid item xs={12} sm={6} md={7} style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <ObjetoAvatar tipo="EXAME" />
                    <Typography variant="h3" className={classes.caseTitle}>
                      {exameSolicitado.exReal?.numeroExame ? `${exameSolicitado.exReal.numeroExame.toUpperCase()}` : 'NÚMERO DO DOCUMENTO: -'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={5} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <MenuAcoes exameSolicitado={exameSolicitado} usuarioState={currentUser} permissaoAcoesExaminador={permissaoAcoesExaminador} permissaoAssumirExame={permissaoAssumirExame} isObjetosAssociados={isObjetosAssociados} listarExameSolicitado={listarExameSolicitado} />
                  </Grid>
                </Grid>

                <Divider style={{ margin: '20px 0 24px 0' }} />

                <Typography variant="subtitle1" className={classes.caseSubtitle} style={{ marginTop: 0, marginBottom: '24px' }}>


                  {exameSolicitado.tipoEx?.nome?.toUpperCase() || 'EXAME SOLICITADO'}
                </Typography>

                <Grid container spacing={3} className={classes.metaGrid} alignItems="stretch">
                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.metaItem}>
                      <AdjustIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>STATUS</Typography>
                        <Typography className={classes.metaValue} style={{ color: exameSolicitado.exReal?.status === 'Cancelado' ? '#D62839' : undefined }}>
                          {exameSolicitado.exReal?.status?.toUpperCase() || '-'}
                        </Typography>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.metaItem}>
                      <LockIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>PRIVACIDADE</Typography>
                        <Typography className={classes.metaValue}>{exameSolicitado.isPrivate ? 'PRIVADO' : 'PÚBLICO'}</Typography>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.metaItem}>
                      <BusinessIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>SETOR RESPONSÁVEL</Typography>
                        <Typography className={classes.metaValue}>{exameSolicitado.setor?.nome?.toUpperCase() || '-'}</Typography>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.metaItem}>
                      <ScheduleIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>DATA DE CADASTRO</Typography>
                        <Typography className={classes.metaValue}>{moment(exameSolicitado.createdAt).format('DD/MM/YYYY HH:mm')}</Typography>
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <SecaoGenerica titulo="Dados Gerais">
              <Grid container spacing={3}>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>SETOR RESPONSÁVEL PELO EXAME</Typography>
                  <Typography className={classes.metaValue}>
                    {exameSolicitado.setor ? `${exameSolicitado.setor.nome.toUpperCase()} / ${exameSolicitado.setor.divisao?.sigla?.toUpperCase()} / ${exameSolicitado.setor.divisao?.regional?.nome?.toUpperCase()} / ${exameSolicitado.setor.divisao?.regional?.orgao?.sigla?.toUpperCase()}` : '-'}
                  </Typography>
                </Grid>
                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>DATA DO CADASTRO</Typography>
                  <Typography className={classes.metaValue}>{moment(exameSolicitado.createdAt).format('LLLL').toUpperCase()}</Typography>
                </Grid>


                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>DATA DA ATRIBUIÇÃO</Typography>
                  <Typography className={classes.metaValue}>{moment(exameSolicitado.exReal.dataInicio).format('LLLL').toUpperCase()}</Typography>
                </Grid>

                <Grid item md={exameSolicitado.quesAdics?.length ? 8 : 4} sm={exameSolicitado.quesAdics?.length ? 8 : 4} xs={12}>
                  <Typography className={classes.metaLabel}>QUESITOS</Typography>
                  <RenderQuesitos quesitos={exameSolicitado.quesAdics} classes={classes} />
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>EXAMINADORES</Typography>
                  {exameSolicitado.exReal?.usuExams?.length ? (
                    <ul className={classes.lista}>
                      {exameSolicitado.exReal.usuExams.map((item) => (
                        <li key={item.id} className={classes.listaItem}>
                          <Typography className={classes.metaValue}>
                            {item.nome?.toUpperCase()} - {item.cargo ? item.cargo.nome.toUpperCase() : ''}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography className={classes.metaValue}>NÃO ATRIBUÍDO</Typography>
                  )}
                </Grid>

                {exameSolicitado.exReal?.status === 'Cancelado' && (

                  <>
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.metaLabel}>MOTIVO DO CANCELAMENTO</Typography>
                      <Typography className={classes.metaValue}>{exameSolicitado.exReal.motivoCancelamento?.toUpperCase() || '-'}</Typography>
                    </Grid>


                    {exameSolicitado.exReal.arqCancs?.length ? (
                      <Grid item xs={12} md={6}>
                        <Typography className={classes.metaLabel} style={{ marginBottom: '8px' }}>
                          ARQUIVO DO CANCELAMENTO DO EXAME
                        </Typography>
                        <Grid container spacing={2}>
                          {exameSolicitado.exReal.arqCancs.map((arquivo) => (
                            <Grid item md={2} sm={4} xs={6} key={arquivo.id}>
                              <ExibirArquivo id={arquivo.id} arquivo={arquivo} rotaLoadFile="/arquivo/vestigio" />
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    ) : (
                      <div />
                    )}

                  </>
                )}

              </Grid>

            </SecaoGenerica>


            <SecaoGenerica titulo="Erratas">
              {exameSolicitado.exReal?.erratas?.length ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow className={classes.tableHeader}>
                        <TableCell>
                          <Typography style={{ fontSize: '13px', fontWeight: 'bold' }}>IDENTIFICADOR</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography style={{ fontSize: '13px', fontWeight: 'bold' }}>EXAMINADOR</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography style={{ fontSize: '13px', fontWeight: 'bold' }}>DATA E HORA DO REGISTRO</Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {exameSolicitado.exReal.erratas.map((item) => (
                        <LinhaTabelaErratas key={item.id} item={item} handleDownloadPDFErrata={handleDownloadPDFErrata} processandoDownload={processandoDownload} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhuma errata registrada
                  </Typography>
                </Grid>
              )}
            </SecaoGenerica>

            {hasDocumentoSolicitacao(exameSolicitado?.tipoEx?.cod) && (
              <SecaoGenerica titulo="Guia de Solicitação">
                <Grid container spacing={2}>
                  <Grid item md={3} sm={6} xs={12}>
                    <BaseCard
                      avatar="EXAME"
                      title={
                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                          Guia de solicitação - <b>{exameSolicitado.assinado ? 'Assinado' : 'Não assinado'}</b>
                        </Typography>
                      }
                      action={
                        <IconButton aria-label="settings" onClick={handleClickBotaoAcoesDocumento}>
                          <MoreVertIcon />
                        </IconButton>
                      }
                    >
                      <CardField label="Número da solicitação" value={exameSolicitado.solEx?.identificador} />
                      <CardField
                        label="Autoridade solicitante"
                        value={
                          exameSolicitado.solEx?.autReq?.nome?.toUpperCase() === 'OUTRO'
                            ? exameSolicitado.solEx?.autoridadeRequisitanteInput
                            : exameSolicitado.solEx?.autReq?.nome || exameSolicitado.solEx?.identificador
                        }
                      />
                    </BaseCard>
                    <StyledMenu id="customized-menu" anchorEl={anchorElDocumento} keepMounted open={Boolean(anchorElDocumento)} onClose={handleCloseMenuDocumento}>
                      <StyledMenuItem onClick={handleClickOpenConfirmarIdentidade} disabled={!verificarUsuarioAutoridadeSolicitante(currentUser?.id)}>
                        <ListItemIcon>
                          <CreateIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Assinar documento" />
                      </StyledMenuItem>
                      <StyledMenuItem onClick={handleDownloadDocumentoExameSolicitado}>
                        <ListItemIcon>
                          <CloudDownloadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Download de documento" />
                      </StyledMenuItem>
                    </StyledMenu>
                  </Grid>
                </Grid>
              </SecaoGenerica>
            )}

            <SecaoGenerica titulo="Informações Adicionadas">
              <Grid item xs={12}>
                <ListaInformacoes tipo="exameSolicitado" id={exameSolicitado.id} />
              </Grid>
            </SecaoGenerica>

            <SecaoGenerica titulo="Dados da Solicitação de Exame">
              <Grid item xs={12}>
                <CardSolicitacaoExame idSolicitacaoExame={exameSolicitado?.id_solicitacao_exame} solicitacao={exameSolicitado?.solEx} omitirExamesSolicitados />
              </Grid>
            </SecaoGenerica>

            <SecaoGenerica titulo="Objetos Examinados">
              {(exameSolicitado.vests && exameSolicitado.vests.length) ||
                (exameSolicitado.cadavs && exameSolicitado.cadavs.length) ||
                (exameSolicitado.perics && exameSolicitado.perics.length) ||
                (exameSolicitado.locais && exameSolicitado.locais.length) ||
                (exameSolicitado.veiculos && exameSolicitado.veiculos.length) ? (
                <Grid container spacing={2}>
                  {exameSolicitado.vests?.map((vestigio) => (
                    <Grid item md={3} sm={6} xs={12} key={vestigio.id}>
                      <CardVestigio vestigio={vestigio} handleClick={handleClickVestigio} />
                    </Grid>
                  ))}
                  {exameSolicitado.cadavs?.map((cadaver) => (
                    <Grid item md={3} sm={6} xs={12} key={cadaver.id}>
                      <CardCadaver cadaver={cadaver} handleClick={handleClickCadaver} />
                    </Grid>
                  ))}
                  {exameSolicitado.perics?.map((periciando) => (
                    <Grid item md={3} sm={6} xs={12} key={periciando.id}>
                      <CardPericiando periciando={periciando} handleClick={handleClickPericiando} />
                    </Grid>
                  ))}
                  {exameSolicitado.locais?.map((local) => (
                    <Grid item md={3} sm={6} xs={12} key={local.id}>
                      <CardLocal local={local} handleClick={handleClickLocal} />
                    </Grid>
                  ))}
                  {exameSolicitado.veiculos?.map((veiculo) => (
                    <Grid item md={3} sm={6} xs={12} key={veiculo.id}>
                      <CardVeiculo veiculo={veiculo} handleClick={handleClickVeiculo} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhum objeto associado ao exame
                  </Typography>
                </Grid>
              )}
            </SecaoGenerica>

            <SecaoGenerica titulo="Caso Relacionado">
              {exameSolicitado?.solEx?.caso ? (
                <Grid container spacing={2}>
                  <Grid item md={3} sm={6} xs={12}>
                    <CardCaso caso={exameSolicitado.solEx.caso} handleClick={handleClickCaso} />
                  </Grid>
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhum caso relacionado
                  </Typography>
                </Grid>
              )}
            </SecaoGenerica>

            {(exameSolicitado.exReal?.tipo === tiposDeExame.CONSTATACAO ||
              exameSolicitado.exReal?.tipo === tiposDeExame.PESQUISA_THC_COCAINA ||
              exameSolicitado.exReal?.tipo === tiposDeExame.QUIMICO_ANABOLIZANTES ||
              exameSolicitado.exReal?.tipo === tiposDeExame.QUIMICO_OUTRAS_SUBSTANCIAS ||
              exameSolicitado.exReal?.tipo === tiposDeExame.QUIMICO_PESQUISA_AGROTOXICOS ||
              exameSolicitado.exReal?.tipo === tiposDeExame.QUIMICO_PESQUISA_SOLVENTES ||
              exameSolicitado.exReal?.tipo === tiposDeExame.QUIMICO_PRODUTO_FARMACEUTICO ||
              exameSolicitado.exReal?.tipo === tiposDeExame.QUIMICO_PESQUISA_INFLAMAVEIS) && (
                <SecaoGenerica titulo="Dados Quantitativos">
                  <Grid item xs={12}>
                    {dadosQuantitativosAgrupados(descricoesTecnicas)?.map((agrupamento) => (
                      <div key={agrupamento.vestigio} style={{ marginBottom: '16px' }}>
                        <Typography variant="body1">
                          <b>Vestígio:</b> {agrupamento.vestigio?.toUpperCase()}
                        </Typography>
                        {agrupamento.descricoes.map((descricao) => (
                          <Typography variant="body2" key={descricao.id} style={{ marginLeft: '16px', marginTop: '4px' }}>
                            <b>Tipo:</b> {getLabelTipoVestigioEspecifico(descricao.tipoVestigioEspecifico)?.toUpperCase()}. <b>Unidades:</b> {descricao.unidades}. <b>Quantidade total:</b> {descricao.descVestQuim?.quantidade} {descricao.descVestQuim?.unidadeQuantidade?.toUpperCase()}.{' '}
                            <b>Resultado da análise:</b> {descricao.anaConstDrogs?.length ? descricao.anaConstDrogs[0].resultado?.toUpperCase() : 'AINDA NÃO FOI REALIZADA NESTE EXAME'}
                          </Typography>
                        ))}
                      </div>
                    ))}

                    {!descricoesTecnicas?.length && (
                      <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                        <Typography variant="body2" color="textSecondary">
                          Ainda não foi realizada análise quantitativa neste exame
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </SecaoGenerica>
              )}

            {!!dadosExameLocal && (
              <SecaoGenerica titulo="Dados do Exame em Local">
                <Grid container spacing={3}>
                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>HORÁRIO DO ACIONAMENTO</Typography>
                    <Typography className={classes.metaValue}>
                      {dadosExameLocal.dataAcionamento ? moment(dadosExameLocal.dataAcionamento).format('LLLL').toUpperCase() : "NÃO INFORMADO"}
                    </Typography>
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>HORÁRIO DE INÍCIO DO DESLOCAMENTO</Typography>
                    <Typography className={classes.metaValue}>
                      {dadosExameLocal.dataDeslocamento ? moment(dadosExameLocal.dataDeslocamento).format('LLLL').toUpperCase() : "NÃO INFORMADO"}
                    </Typography>
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>HORÁRIO DE CHEGADA AO LOCAL</Typography>
                    <Typography className={classes.metaValue}>
                      {dadosExameLocal.dataChegada ? moment(dadosExameLocal.dataChegada).format('LLLL').toUpperCase() : "NÃO INFORMADO"}
                    </Typography>
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>HORÁRIO DE CONCLUSÃO DO EXAME</Typography>
                    <Typography className={classes.metaValue}>
                      {dadosExameLocal.dataConclusao ? moment(dadosExameLocal.dataConclusao).format('LLLL').toUpperCase() : "NÃO INFORMADO"}
                    </Typography>
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>AUTORIDADE POLICIAL PRESENTE</Typography>
                    <Typography className={classes.metaValue}>
                      {dadosExameLocal?.anaLocs?.filter(l => l?.classificacao === "IMEDIATO")?.[0]?.autoridadePolicialPresente?.toUpperCase() || 'NÃO INFORMADO'}
                    </Typography>
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>OUTRAS INFORMAÇÕES</Typography>
                    <Typography className={classes.metaValue}>
                      <Link style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={dadosExameLocal?.id ? () => handleClickExameLocal(dadosExameLocal?.id) : () => {}}>
                        VER MAIS
                      </Link>
                    </Typography>
                  </Grid>
                </Grid>

              </SecaoGenerica>
            )}

            <SecaoGenerica titulo="Histórico de Operações">
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                {exameSolicitado?.id && <HistoricoOperacoes id={exameSolicitado.id} tipo="EXAME_SOLICITADO" refresh={refreshHistorico} />}
              </Grid>
            </SecaoGenerica>
          </>
        )}
      </div>

      <ConfirmarIdentidade aberto={openConfirmarIdentidade} setAberto={setOpenConfirmarIdentidade} callback={handleClickAssinarDocumento} titulo="Assinar Documento" />
    </Page>
  )
}

const LinhaTabelaErratas = (props) => {
  const { item, handleDownloadPDFErrata, processandoDownload } = props
  const classes = useStyles()

  return (
    <TableRow className={classes.tableRow} style={{ width: '100%' }}>
      <TableCell style={{ width: '15%' }}>
        <Typography style={{ fontSize: '13px' }}>{item.identificador?.toUpperCase() || ''}</Typography>
      </TableCell>
      <TableCell style={{ width: '40%' }}>
        <Typography style={{ fontSize: '13px' }}>{item.usu?.nome?.toUpperCase() || ''}</Typography>
      </TableCell>

      <TableCell>
        <Typography style={{ fontSize: '13px' }}>{moment(item.createdAt).format('LLLL').toUpperCase()}</Typography>
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" onClick={() => handleDownloadPDFErrata(item)} disabled={processandoDownload}>
          <CloudDownload fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  )
}
